import {ViewedFileProgress} from './ViewedFileProgress'
import {Stack} from '@primer/react'
import {useState} from 'react'
import stickyStyles from '@github-ui/use-sticky-header/use-sticky-header.module.css'
import {useIntersectionObserver} from '@github-ui/use-sticky-header/useIntersectionObserver'
import {clsx} from 'clsx'
import styles from './PullRequestFilesToolbar.module.css'
import {useIsSingleFileMode} from '../../stores/selectors'
import {ReviewNavigationButtonGroup} from './ReviewNavigationButtonGroup'
import {DiffViewSettings, type AvailableDiffViewSettings} from '@github-ui/diff-view-settings'
import {DiffCompactLinesPopover} from '@github-ui/diff-view-settings/components/DiffCompactLinesPopover'
import {useIsNoticeDismissed} from '../../stores/layout-selectors'
import {useDismissNotice} from '../../mutations/use-dismiss-notice'

export type BasicDiffComparisonViewToolbarProps = {
  fileFilter: React.ReactElement
  isFileTreeExpanded?: boolean
  shouldShowViewedFilesCount: boolean
  totalFilesCount: number
  treeToggleElement?: React.ReactElement
}

export function BasicDiffComparisonViewToolbar({
  fileFilter,
  isFileTreeExpanded,
  shouldShowViewedFilesCount,
  totalFilesCount,
  treeToggleElement,
}: BasicDiffComparisonViewToolbarProps) {
  const enabledDiffViewSettings: AvailableDiffViewSettings[] = ['line-spacing'] as const
  const [isSticky, setIsSticky] = useState(false)

  const isSingleFileMode = useIsSingleFileMode()
  const showDivider = shouldShowViewedFilesCount || !isFileTreeExpanded

  const sensorRef = useIntersectionObserver(entries => {
    if (entries[0]) {
      setIsSticky(!entries[0].isIntersecting)
    }
  })

  return (
    <>
      <div ref={sensorRef} className={styles.stickyHeaderActivationThreshold} />
      <Stack
        as="section"
        direction="horizontal"
        justify="space-between"
        gap="condensed"
        align="center"
        className={clsx(stickyStyles.stickyHeader, styles['simple-toolbar'], isSticky && styles['is-stuck'])}
      >
        {/* eslint-disable-next-line @github-ui/dotcom-primer/no-title-case -- baselined during linter rollout */}
        <h2 className="sr-only">Diff Comparison Toolbar</h2>

        <Stack direction="horizontal" gap="condensed" align="center" className="min-width-0">
          <div>{treeToggleElement}</div>
        </Stack>
        <Stack direction="horizontal" align="center">
          <div className={styles['file-controls']}>
            {shouldShowViewedFilesCount && <ViewedFileProgress totalFilesCount={totalFilesCount} />}

            {!isFileTreeExpanded && fileFilter}
            {isSingleFileMode && totalFilesCount > 1 && (
              <div className={styles['file-controls-navigation']}>
                <ReviewNavigationButtonGroup />
              </div>
            )}
          </div>

          {showDivider && <div className={styles['file-controls-divider']} data-testid="file-controls-divider" />}

          <DiffViewSettings invisible={false} small enabledDiffViewSettings={enabledDiffViewSettings} />
          <DiffCompactLinesPopover useIsNoticeDismissed={useIsNoticeDismissed} useDismissNotice={useDismissNotice} />
        </Stack>
      </Stack>
    </>
  )
}
